import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import { useForm, getPurgedEmbedImages, isFormWithHtmlDirty } from '../../helpers'

import { useUser } from '../../contexts'

import {
  EditorWithPreview,
  HtmlEditor,
  LeavingOnDirtyForm,
  PreviewPage,
  VideoSelector
} from '../specialized-components'
import { icons, Button, Card, ExternalLinkButton, FormField, Icon, Input, SubmitButton } from '../ui-blocks'

import topicFields from './topic-fields'


const parseValues = topic => ({
  title: topic.title,
  content: topic.content,
  featuredMedia: topic.featuredMedia,
  embedImages: []
})


export const EditTopic = ({ topic, onUpdate, onDelete }) => {
  const [form, setFormValues, validateForm] = useForm(topicFields)
  const { userAvatar } = useUser()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setFormValues(parseValues(topic))
  }, [topic]) // eslint-disable-line react-hooks/exhaustive-deps


  const handleUpdate = async () => {
    const isFormValid = validateForm()

    if (!isFormValid) {
      return
    }

    setIsLoading(true)

    await onUpdate({
      ...form.values,
      embedImages: getPurgedEmbedImages(form.values.content, form.values.embedImages)
    })

    setIsLoading(false)
  }

  const handleAddImageToUpload = media => {
    form.fields.embedImages.setValue([...form.fields.embedImages.value, media])
  }


  if (!Object.keys(form.values).length) {
    return null
  }


  const isNew = !topic.id
  const isFormDirty = isFormWithHtmlDirty(form)

  const media = form.fields.featuredMedia.value


  return (
    <>
      <LeavingOnDirtyForm isDirty={isFormDirty} />

      <div className="editor-header">
        <h1>{isNew ? 'New topic' : (form.values.title || 'Edit topic')}</h1>

        <div className="d-flex justify-content-end align-items-center">
          {!isNew && (
            <ExternalLinkButton
              href={topic.link}
              className="mr-3"
              variant="secondary"
              disabled={isFormDirty}
            >
              Open live page
            </ExternalLinkButton>
          )}
          <SubmitButton
            onClick={handleUpdate}
            isLoading={isLoading}
            isValid={form.isValid}
          >
            {isNew ? 'Create' : 'Update'}
          </SubmitButton>
        </div>
      </div>

      <EditorWithPreview preview={(
        <PreviewPage
          kicker={topic.courses?.[0]?.title ? `Topic in ${topic.courses[0].title}` : 'Topic'}
          title={form.values.title || ''}
          byline={userAvatar?.name ? `By ${userAvatar.name}` : ''}
          media={media}
          html={form.values.content || ''}
        />
      )}>
        <FormField label="Title" inputField={form.fields.title} className="mb-3">
          <Input
            id="title"
            className="form-control-lg"
            placeholder="What learners will see as the topic name"
            {...form.fields.title?.getStatusProps()}
            {...form.fields.title?.getProps()}
          />
        </FormField>

        {!isNew && (
          <p className="editor-note">
            {(!!topic.courses.length) ? (
              <>
                In
                {topic.courses.map((course, i) => (
                  <span key={course.id}>
                    {(i > 0) && ','}
                    <Link to={`/courses/${course.id}`} className="ml-1">
                      {course.title}
                    </Link>
                  </span>
                ))}
              </>
            ) : (
              <span>Not in a course yet. Add it from a course's lessons.</span>
            )}
          </p>
        )}

        <Card title="Video" className="mb-5" imageProps={media?.previewUrl ? {
          src: media.previewUrl,
          alt: 'Topic video'
        } : {}}>
          <VideoSelector media={media} setMedia={form.fields.featuredMedia.setValue} hasClearButton />
        </Card>

        <Card title="Topic content" className="html-editor-large-content mb-5">
          <HtmlEditor
            {...form.fields.content?.getProps()}
            addImageToUpload={handleAddImageToUpload}
          />
        </Card>

        {!isNew && (
          <Button
            onClick={onDelete}
            variant="outline-danger" className="btn-danger-on-hover px-3"
          >
            <Icon icon={icons.delete} className="mr-2" />
            Delete this topic
          </Button>
        )}
      </EditorWithPreview>
    </>
  )
}


EditTopic.propTypes = {
  topic: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}
